import { Organisation } from '@futrli-sdk/organisations';
import { getWithContextHOC } from '@futrli/shared-services';
import { createContext, useContext } from 'react';
import { FinancialConfig } from '@futrli-sdk/predict';

export interface IPredictManagementContext {
  organisation: Organisation | null;
  financialConfig?: FinancialConfig;
  currency: string | null;
  refetchOrganisation: () => void;
  addErrorMessage: (error: Error) => void;
  isFrenchAccounting?: boolean;
  googleSheetImportAllowed?: boolean;
}

export type PredictManagementContextKeys = keyof IPredictManagementContext;

const DEFAULT_OPTIONS: PredictManagementContextKeys[] = ['organisation', 'currency'];

/**
 * ProductManagement context initilised to be shared.
 *
 * NOTE: The context is comprise of all properties we want to share between the parent
 * and lower level nodes and the nodes between them do not need to know about these properties.
 */
export const PredictManagementContext = createContext<Partial<IPredictManagementContext>>({
  organisation: null,
  currency: null
});

/**
 * Injects the ProductManagement context to the component passed as param.
 * @name withPredictContext
 * @function
 * @param {React.ComponentType<RC & CP>} Component - Component to wrap with the consumer.
 * @param {Array<keyof C>} options - Context props to inject into the component.
 * @template {RC} - React component props without context props.
 * @template {CP} - Context props of the component.
 *
 * @returns {React.ComponentType<RC>} - Wrapped component.
 */
export const withPredictManagementContext = getWithContextHOC(PredictManagementContext, DEFAULT_OPTIONS);

export const usePredictManagementContext = () => useContext(PredictManagementContext);
